import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import packageInfo from '../../../../../../../package.json';
import { environment } from '../../../../../../environments/environment';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
    ApiResponse,
    ApiServiceInterface,
    FormServiceInterface,
    OfflineQueue,
    OfflineServiceInterface,
    PushServiceInterface,
    ThemeServiceInterface,
    UserAccountInterface,
    UserServiceInterface,
} from '@hutsix/ngxh6';
import { ToastService } from '@hutsix/ngxh6';

interface NavLink {
    label: string;
    link: string;
    exact: boolean;
}

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {
    public env: string = environment.env;
    public version: string = packageInfo.version + (environment.env === 'staging' ? '-staging' : '');
    public _user: { loggedIn: boolean; account?: UserAccountInterface };
    public offlineQueue$: Observable<OfflineQueue[]>;
    /** The information-hub pages that opt into the menu (published + "show in menu"). */
    public pages: { title: string; slug: string }[] = [];

    /** Mobile hamburger state (below lg). Opened on tap, closed on navigate. */
    public navCollapsed = true;

    /** Every link the menu shows, in order. Rebuilt when the pages or the signed-in user change. */
    public navLinks: NavLink[] = [];
    /**
     * At lg+ (the inline bar), how many links render inline before the rest fold into the "More"
     * dropdown - narrowed by measureAndFold() so the desktop bar can never overflow however many
     * pages TLC add (the config-tabs "More" behaviour). Below lg every link shows, stacked in the
     * scrollable hamburger panel, so nothing folds there.
     */
    public visibleCount = 999;
    /** Stays false until the first measure lands, so the inline links don't flash un-folded on load. */
    public ready = false;

    /** The visible link row - at lg+ its width is the space the inline links have to fit into. */
    @ViewChild('navRow') private navRow?: ElementRef<HTMLElement>;
    /** A hidden copy of every link at natural width, measured to decide how many fit inline. */
    @ViewChildren('measureItem') private measureItems?: QueryList<ElementRef<HTMLElement>>;

    private itemWidths: number[] = [];
    /** Width reserved for the "More" toggle when anything is folded. A slight over-reserve is safe. */
    private readonly moreWidth = 92;
    private resizeObserver?: ResizeObserver;
    private breakpointMq?: MediaQueryList;
    private readonly onBreakpointChange = (): void => this.scheduleFold();
    private readonly destroyed$ = new Subject<void>();

    constructor(
        public router: Router,
        public toastr: ToastService,
        @Inject('UserService') public userService: UserServiceInterface,
        @Inject('FormService') public formService: FormServiceInterface,
        @Inject('OfflineService') public offline: OfflineServiceInterface,
        @Inject('PushService') public push: PushServiceInterface,
        @Inject('ApiService') public api: ApiServiceInterface,
        @Inject('ThemeService') public themeService: ThemeServiceInterface,
        public cdRef: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        if (this._user) {
            this.offlineQueue$ = this.offline.getOfflineQueue();
        }
    }

    ngOnInit(): void {
        // The public information menu. Guest GET (skipAuth + bypassOfflineQueue, like the
        // other public calls). If it fails, the nav simply carries no page links.
        this.api.get({ url: '/api/public/pages/menu', skipAuth: true, bypassOfflineQueue: true, useCache: false, displayErrors: false }).subscribe(
            (res: ApiResponse<{ title: string; slug: string }[]>) => {
                this.pages = res.data ?? [];
                this.rebuildLinks();
            },
            () => {
                /* no menu on error */
            },
        );

        // "My Permits" is hidden from staff/business accounts, so the link set depends on who's
        // signed in - rebuild (and re-fold) whenever that changes.
        this.userService.watch.pipe(takeUntil(this.destroyed$)).subscribe(() => this.rebuildLinks());

        // Close the mobile menu after any navigation so it doesn't linger open over the new page.
        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                takeUntil(this.destroyed$),
            )
            .subscribe(() => {
                this.navCollapsed = true;
                this.cdRef.markForCheck();
            });
    }

    ngAfterViewInit(): void {
        // Re-measure whenever the link set changes (pages loaded, user switched).
        this.measureItems?.changes.pipe(takeUntil(this.destroyed$)).subscribe(() => this.scheduleFold());

        // Re-fold whenever the bar's width changes. Kept outside Angular so a resize storm doesn't
        // spin change detection; scheduleFold() re-enters the zone for the one update that matters.
        this.zone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => this.scheduleFold());
            if (this.navRow) {
                this.resizeObserver.observe(this.navRow.nativeElement);
            }
        });

        // Recompute when crossing the lg breakpoint (hamburger below <-> inline + fold above).
        this.breakpointMq = window.matchMedia('(min-width: 992px)');
        this.breakpointMq.addEventListener('change', this.onBreakpointChange);

        this.scheduleFold();
        // Web fonts can land after the first measure and change text widths; re-fold once they do.
        (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts?.ready?.then(() => this.scheduleFold());
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.breakpointMq?.removeEventListener('change', this.onBreakpointChange);
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private rebuildLinks(): void {
        const links: NavLink[] = [
            { label: 'Home', link: '/', exact: true },
            { label: 'Apply for a permit', link: '/apply', exact: false },
            ...this.pages.map(p => ({ label: p.title, link: '/' + p.slug, exact: false })),
        ];
        // Applicants (not staff/business) get My Permits; see the old template note.
        if (!this.userService.isGranted(['ROLE_ADMIN', 'ROLE_STAFF', 'ROLE_BUSINESS'])) {
            links.push({ label: 'My Permits', link: '/my-permits', exact: false });
        }
        this.navLinks = links;
        // Show everything until the measurer re-renders and scheduleFold narrows it (lg+ only).
        this.visibleCount = links.length;
        this.cdRef.markForCheck();
        this.scheduleFold();
    }

    /** lg breakpoint (Bootstrap 992px). Below it the links live in the hamburger panel. */
    private isDesktop(): boolean {
        return this.breakpointMq?.matches ?? window.innerWidth >= 992;
    }

    /** Defer a fold to the next frame, back inside Angular, so it never fights the current CD pass. */
    private scheduleFold(): void {
        this.zone.runOutsideAngular(() => {
            requestAnimationFrame(() => this.zone.run(() => this.measureAndFold()));
        });
    }

    private measureAndFold(): void {
        // Below lg the links are in the scrollable hamburger panel - nothing folds, show them all.
        if (!this.isDesktop()) {
            if (this.visibleCount !== this.navLinks.length || !this.ready) {
                this.visibleCount = this.navLinks.length;
                this.ready = true;
                this.cdRef.markForCheck();
            }
            return;
        }

        const items = this.measureItems?.toArray() ?? [];
        if (items.length === 0) {
            return;
        }
        this.itemWidths = items.map(i => i.nativeElement.offsetWidth);

        const avail = this.navRow?.nativeElement.clientWidth ?? 0;
        if (avail <= 0) {
            return;
        }

        const total = this.itemWidths.reduce((a, b) => a + b, 0);
        let count: number;
        if (total <= avail) {
            count = this.navLinks.length; // everything fits - no "More"
        } else {
            let used = this.moreWidth; // reserve the toggle, then fit what's left
            count = 0;
            for (const w of this.itemWidths) {
                if (used + w <= avail) {
                    used += w;
                    count++;
                } else {
                    break;
                }
            }
        }

        if (count !== this.visibleCount || !this.ready) {
            this.visibleCount = count;
            this.ready = true;
            this.cdRef.markForCheck();
        }
    }

    update(): void {
        if (this._user) {
            this.offlineQueue$ = this.offline.getOfflineQueue();
        }
    }

    clearCaches(): void {
        if (!confirm('All cached data will be removed. You will be required to re-sync to use offline capabilities. Are you sure?')) return;

        this.api.clearCaches().subscribe(res => {
            if (res) {
                this.toastr.success('Caches cleared!');
            } else {
                this.toastr.error('Try again.', 'Something went wrong.');
            }
        });
    }
}
